import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Music, Video, Plus, Trash2, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { uploadFile } from "@/lib/upload";
import { formatApiError } from "@/lib/api";
import LanguageSelect from "./LanguageSelect";
import { inputCls, labelCls } from "./constants";

function TrackAudioUpload({ track, onUploaded, idx }) {
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null); // { sample_rate, bit_depth, channels }
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file, "audio");
      onUploaded(res.url);
      setMeta({ sample_rate: res.sample_rate, bit_depth: res.bit_depth, channels: res.channels });
      const rateKhz = res.sample_rate ? `${(res.sample_rate / 1000).toString().replace(/\.0$/, "")} kHz` : "";
      toast.success(`WAV accepted — ${res.bit_depth}-bit · ${rateKhz}`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const uploaded = !!track.audio_url;
  return (
    <label className="cursor-pointer rounded-xl border border-dashed border-white/15 hover:border-white/30 bg-white/[0.02] px-4 py-3 flex items-center gap-3 text-sm transition-colors">
      {busy ? <Loader2 size={16} className="animate-spin text-slate-400" /> : <Music size={16} className={uploaded ? "text-blue-400" : "text-slate-400"} />}
      <span className={uploaded ? "text-blue-400" : "text-slate-400"}>
        {uploaded
          ? (meta ? `Audio master — ${meta.bit_depth}-bit · ${(meta.sample_rate / 1000).toString().replace(/\.0$/, "")} kHz` : "Audio master uploaded")
          : "Upload audio master — WAV only, 16/24/32-bit, 44.1 / 48 / 192 kHz"}
      </span>
      <input data-testid={`track-audio-${idx}`} type="file" accept=".wav,audio/wav,audio/x-wav" className="hidden" onChange={onFile} />
    </label>
  );
}

function TrackVideoUpload({ track, onUploaded, idx }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file, "video");
      onUploaded(res.url);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const uploaded = !!track.audio_url;
  return (
    <label
      data-testid={`track-video-picker-${idx}`}
      className={`cursor-pointer rounded-xl border border-dashed px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
        uploaded
          ? "border-fuchsia-500/40 bg-fuchsia-500/[0.05]"
          : "border-fuchsia-500/25 bg-fuchsia-500/[0.02] hover:border-fuchsia-500/40"
      }`}
    >
      {busy ? <Loader2 size={16} className="animate-spin text-slate-400" /> : <Video size={16} className={uploaded ? "text-fuchsia-300" : "text-fuchsia-400"} />}
      <span className={uploaded ? "text-fuchsia-200" : "text-slate-300"}>
        {uploaded ? "Video uploaded — good to go" : "Upload your music video (MP4 / MOV / WebM) — required"}
      </span>
      <input data-testid={`track-video-${idx}`} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v" className="hidden" onChange={onFile} />
    </label>
  );
}

export default function TrackRow({ idx, track, onChange, onDelete, isVideo = false }) {
  const set = (k, v) => onChange({ ...track, [k]: v });
  const [openLyrics, setOpenLyrics] = useState(false);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid={`track-row-${idx}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <span className="rounded-full bg-white/10 text-white font-medium px-2.5 py-0.5">Track {track.track_number || idx + 1}</span>
          {track.explicit && <span className="rounded bg-red-500/20 text-red-300 px-1.5 py-0.5">EXPLICIT</span>}
        </div>
        <button
          type="button"
          data-testid={`track-delete-${idx}`}
          onClick={onDelete}
          className="text-slate-400 hover:text-red-400 p-1.5"
          aria-label="Remove track"
        >
          <Trash2 size={15} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Title</label>
          <input data-testid={`track-title-${idx}`} className={inputCls} value={track.title} onChange={(e) => set("title", e.target.value)} placeholder="Track title" />
        </div>
        <div>
          <label className={labelCls}>Version</label>
          <input className={inputCls} value={track.version} onChange={(e) => set("version", e.target.value)} placeholder="e.g. Radio Edit" />
        </div>
        <div>
          <label className={labelCls}>Display Artist</label>
          <input className={inputCls} value={track.display_artist} onChange={(e) => set("display_artist", e.target.value)} placeholder="Displayed on stores" />
        </div>
        <div>
          <label className={labelCls}>ISRC</label>
          <input className={inputCls} value={track.isrc || ""} onChange={(e) => set("isrc", e.target.value)} placeholder="Leave blank if this is a new track" />
          <div className="mt-1 text-[10px] text-slate-500">Only fill this in if the recording was previously released with an existing ISRC.</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className={labelCls}>Track #</label>
            <input type="number" min={1} className={inputCls} value={track.track_number} onChange={(e) => set("track_number", parseInt(e.target.value || "1", 10))} />
          </div>
          <div>
            <label className={labelCls}>Volume</label>
            <input type="number" min={1} className={inputCls} value={track.volume} onChange={(e) => set("volume", parseInt(e.target.value || "1", 10))} />
          </div>
          <div>
            <label className={labelCls}>Length (s)</label>
            <input type="number" min={0} className={inputCls} value={track.length_seconds} onChange={(e) => set("length_seconds", parseInt(e.target.value || "0", 10))} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Audio Language</label>
          <LanguageSelect value={track.audio_language} onChange={(v) => set("audio_language", v)} testId={`track-lang-${idx}`} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-5 pt-1">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch data-testid={`track-explicit-${idx}`} checked={!!track.explicit} onCheckedChange={(v) => set("explicit", v)} /> Explicit lyrics
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch checked={!!track.continuous_mix} onCheckedChange={(v) => set("continuous_mix", v)} /> Continuous mix
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch checked={!!track.album_only} onCheckedChange={(v) => set("album_only", v)} /> Album only
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch checked={!!track.withhold_mechanicals} onCheckedChange={(v) => set("withhold_mechanicals", v)} /> Withhold mechanicals
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Writers</label>
          <div className="space-y-2">
            {track.writers.map((w, i) => (
              <div key={i} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                <input className={inputCls} placeholder="Writer name" value={w.name} onChange={(e) => {
                  const copy = [...track.writers]; copy[i] = { ...copy[i], name: e.target.value }; set("writers", copy);
                }} />
                <input className={inputCls} placeholder="Role" value={w.role} onChange={(e) => {
                  const copy = [...track.writers]; copy[i] = { ...copy[i], role: e.target.value }; set("writers", copy);
                }} />
                <button type="button" onClick={() => set("writers", track.writers.filter((_, j) => j !== i))} className="rounded-xl border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-slate-400 hover:text-red-400 px-3">
                  <X size={14} />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => set("writers", [...track.writers, { name: "", role: "Lyricist/Composer" }])} className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300">
              <Plus size={14} /> Add writer
            </button>
          </div>
        </div>
        <div>
          <label className={labelCls}>Publishers / PROs</label>
          <div className="space-y-2">
            {track.publishers.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                <input className={inputCls} placeholder="Publisher" value={p.name} onChange={(e) => {
                  const copy = [...track.publishers]; copy[i] = { ...copy[i], name: e.target.value }; set("publishers", copy);
                }} />
                <input className={inputCls} placeholder="PRO (BMI/ASCAP/…)" value={p.pro} onChange={(e) => {
                  const copy = [...track.publishers]; copy[i] = { ...copy[i], pro: e.target.value }; set("publishers", copy);
                }} />
                <button type="button" onClick={() => set("publishers", track.publishers.filter((_, j) => j !== i))} className="rounded-xl border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-slate-400 hover:text-red-400 px-3">
                  <X size={14} />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => set("publishers", [...track.publishers, { name: "", pro: "" }])} className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300">
              <Plus size={14} /> Add publisher
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className={labelCls}>Producers</label>
        <input className={inputCls} value={track.producers} onChange={(e) => set("producers", e.target.value)} placeholder="Comma-separated" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>C Line (composition ©)</label>
          <input className={inputCls} value={track.c_line} onChange={(e) => set("c_line", e.target.value)} placeholder="© 2026 Owner" />
        </div>
        <div>
          <label className={labelCls}>P Line (recording ℗)</label>
          <input className={inputCls} value={track.p_line} onChange={(e) => set("p_line", e.target.value)} placeholder="℗ 2026 Owner" />
        </div>
      </div>

      <div>
        <button type="button" onClick={() => setOpenLyrics((v) => !v)} className="text-xs text-blue-400 hover:text-blue-300">
          {openLyrics ? "Hide lyrics" : "Add lyrics"}
        </button>
        {openLyrics && (
          <textarea
            className={inputCls + " min-h-[100px] mt-2"}
            value={track.lyrics}
            onChange={(e) => set("lyrics", e.target.value)}
            placeholder="Paste the track lyrics here"
          />
        )}
      </div>

      {isVideo ? (
        <TrackVideoUpload track={track} onUploaded={(url) => set("audio_url", url)} idx={idx} />
      ) : (
        <TrackAudioUpload track={track} onUploaded={(url) => set("audio_url", url)} idx={idx} />
      )}
    </div>
  );
}
